import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import ErrorModal from "../../utils/ErrorModal";
import { uploadFile } from "../../utils/uploadToS3";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ButtonBack from "../../utils/ButtonBack";
import { PhoneInput } from "react-international-phone";
import { getCountryCallingCode } from "react-phone-number-input";
import CountrySelect from "../../utils/countrySelect";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedSelect from "../../utils/ThemedSelect";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  organization: "#2F7FD1",
  admin: "#12A08F",
  subscription: "#6D5DE8",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: 38, px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: 38, px: 2, borderRadius: "10px", background: "#FFFFFF", color: INK, border: `1px solid ${FIELD_EDGE}`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", "&:hover": { background: "#F4F7FA" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
`;

const CreateOrg = () => {
  const [org, setOrg] = useState();
  const user = JSON.parse(localStorage.getItem("user"));
  const { orgId } = useParams();
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [selectedPlan, setSelectedPlan] = useState({
    key: "PRO",
    name: "Pro",
    price: 0,
  });
  const [paymentProof, setPaymentProof] = useState(null);
  const [freeLimits, setFreeLimits] = useState();
  const [basicLimits, setBasicLimits] = useState();
  const [proLimits, setProLimits] = useState();
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("error");
  const [country, setCountry] = useState("IN");
  const isMobile = useMediaQuery("(max-width: 750px)");

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/subscription-limit`,
        );
        const limits = res.data || [];
        setFreeLimits(limits.find((l) => l.type === "FREE") || null);
        setBasicLimits(limits.find((l) => l.type === "BASIC") || null);
        setProLimits(limits.find((l) => l.type === "PRO") || null);
      } catch (err) {
        console.error("Failed to fetch limits", err);
      }
    };
    fetchLimits();
  }, []);

  const PLANS = [
    {
      key: "FREE",
      name: "Free",
      price: 0,
      features: [
        `${freeLimits?.leadLimit || 0} Leads Creation`,
        `${freeLimits?.projectLimit || 0} Project Creation`,
        "Basic CRM",
      ],
    },
    {
      key: "BASIC",
      name: "Basic",
      price: 999,
      features: [
        `${basicLimits?.leadLimit || 0} Leads Creation`,
        `${basicLimits?.projectLimit || 0} Project Creation`,
        "Basic CRM",
        "Lead Structure",
      ],
    },
    {
      key: "PRO",
      name: "Pro",
      price: 2499,
      features: [
        `${proLimits?.leadLimit || 0} Leads Creation`,
        `${proLimits?.projectLimit || 0} Project Creation`,
        "Basic CRM",
        "Lead Structure",
        "Custom Fields",
      ],
    },
  ];

  const handleChange = (e) => {
    const updatedValue =
      e.target.name === "pan" ? e.target.value.toUpperCase() : e.target.value;
    setOrg({ ...org, [e.target.name]: updatedValue });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    const field = e.target.name;

    if (!file) return;

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadFile(file, "Payments");
      setPaymentProof(res);
    } catch (error) {
      console.log(error);
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const data = org || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isEmpty(data.orgName)) newErrors.orgName = "Org Name is required";

    if (isEmpty(data.pan)) {
      newErrors.pan = "PAN is required";
    } else if (!panRegex.test(data.pan)) {
      newErrors.pan = "Invalid PAN format (e.g. ABCDE1234F)";
    }

    if (isEmpty(data.adminName)) newErrors.adminName = "Admin Name is required";

    if (isEmpty(data.adminEmail)) {
      newErrors.adminEmail = "Admin Email ID is required";
    } else if (!emailRegex.test(data.adminEmail)) {
      newErrors.adminEmail = "Invalid email address";
    }

    // if (isEmpty(data.adminMobile)) {
    //   newErrors.adminMobile = "Admin Mobile Number is required";
    // } else if (!mobileRegex.test(data.adminMobile)) {
    //   newErrors.adminMobile = "Invalid mobile number (must be 10 digits)";
    // }

    if (user) {
      const startDate = data.subscriptionStartDate
        ? new Date(data.subscriptionStartDate)
        : null;
      const endDate = data.subscriptionEndDate
        ? new Date(data.subscriptionEndDate)
        : null;
      if (!startDate) {
        newErrors.subscriptionStartDate = "Subscription Start Date is required";
      } else if (startDate < today) {
        newErrors.subscriptionStartDate = "Start date cannot be in the past";
      }

      if (!endDate) {
        newErrors.subscriptionEndDate = "Subscription End Date is required";
      } else if (endDate < today) {
        newErrors.subscriptionEndDate = "End date cannot be in the past";
      } else if (startDate && endDate <= startDate) {
        newErrors.subscriptionEndDate = "End date must be after start date";
      }
    }

    // if (!user && selectedPlan.key !== "FREE") {
    //   if (isEmpty(paymentProof))
    //     newErrors.paymentProof = "Uploading screenshot/receipt is required";
    // }

    setErrors(newErrors);
    console.log(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setBtnLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/organization`, {
        orgName: org.orgName,
        adminEmail: org.adminEmail,
        adminName: org.adminName,
        adminMobile: org.adminMobile.replace(/\D/g, ""),
        countryCode: getCountryCallingCode(country),
        pan: org.pan,
        subscriptionStartDate: org.subscriptionStartDate,
        subscriptionEndDate: org.subscriptionEndDate,
        type: selectedPlan?.key,
        receipt: paymentProof,
      });
      setModalType("success");
      setModalMsg("Organization created successfully");
      setModalOpen(true);
    } catch (error) {
      setModalType("error");
      setModalMsg(error?.response?.data.Exception || "Server error");
      setModalOpen(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    // FIX: show "Updating..." while the save is in progress
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${org.orgId}`,
        {
          orgName: org.orgName,
          adminEmail: org.adminEmail,
          adminName: org.adminName,
          adminMobile: org.adminMobile.replace(/\D/g, ""),
          countryCode: getCountryCallingCode(country),
          pan: org.pan,
          subscriptionStartDate: org.subscriptionStartDate,
          subscriptionEndDate: org.subscriptionEndDate,
          subscription: org.subscription,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setModalType("success");
      setModalMsg("Organization updated successfully");
      setModalOpen(true);
    } catch (error) {
      console.log(error);
      setModalType("error");
      setModalMsg(error?.response?.data?.Exception || "Server error");
      setModalOpen(true);
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    if (orgId !== undefined) {
      const getOrg = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${user.accessToken}` } },
          );
          setOrg(res.data);
        } catch (error) {
          console.log(error);
        }
      };

      getOrg();
    }
  }, []);

  const handleBack = () => {
    navigate("/superadmin-dashboard");
  };

  const handleModalClose = () => {
    setModalOpen(false);

    if (modalType === "success") {
      navigate("/superadmin-dashboard");
    }
  };

  /* ---------- markup helpers (same handlers as before) ---------- */
  const isEdit = !!(org && org.orgId);

  const fieldBox = (id, label, required, control, err, extra) => (
    <Box key={id}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && <span style={req}>*</span>}
      </label>
      {control}
      {extra}
      {err && <p className="error" style={errorStyle}>{err}</p>}
    </Box>
  );

  const section = (id, title, meta, n, children) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[id])} />
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(n)}>{children}</Box>
      </Box>
    </section>
  );

  const saveButton = isEdit ? (
    <Button
      className="btn-contained"
      onClick={handleUpdate}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Updating..." : "Update Organization"}
    </Button>
  ) : (
    <Button
      className="btn-contained"
      onClick={handleSubmit}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Creating..." : "Create Organization"}
    </Button>
  );

  const subtitle = isEdit
    ? org.orgName
    : user
      ? "Add an organization and its admin"
      : "Set up your organization to get started";

  const nCols = user ? 3 : 2;

  return (
    <>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-org"
        style={{
          position: "relative",
          fontFamily: FONT,
          color: INK,
          textAlign: "left",
          padding: isMobile && user ? "4px 0 88px" : "4px 0 32px",
          ...(user ? {} : { maxWidth: 760, margin: "0 auto", paddingInline: 16 }),
        }}
      >
        {btnLoading && <div className="loading-div"></div>}

        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5, mt: user ? 0 : 2 }}>
          <div style={{ minWidth: 0 }}>
            {user && (
              <button
                type="button"
                onClick={handleBack}
                style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
              >
                ← Organizations
              </button>
            )}
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
            >
              {isEdit ? "Edit organization" : "New organization"}
            </Typography>
            {subtitle && (
              <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {subtitle}
              </div>
            )}
          </div>
          {user && !isMobile && (
            <Box sx={{ display: "flex", gap: 1.25, flex: "0 0 auto" }}>
              <Button onClick={handleBack} sx={ghostBtn}>Cancel</Button>
              {saveButton}
            </Box>
          )}
        </Box>

        <div className="border-container">
          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* ---------------- ORGANIZATION ---------------- */}
            {section("organization", "Organization", "2 required", nCols, [
              fieldBox(
                "orgName",
                "Org Name",
                true,
                <input
                  type="text"
                  id="orgName"
                  name="orgName"
                  placeholder="Organization name"
                  onChange={handleChange}
                  value={org && org.orgName}
                  className={`${errors.orgName && "input-error"}`}
                  style={inputStyle(!!errors.orgName)}
                />,
                errors.orgName,
              ),
              fieldBox(
                "pan",
                "PAN",
                true,
                <input
                  type="text"
                  id="pan"
                  name="pan"
                  placeholder="ABCDE1234F"
                  onChange={handleChange}
                  value={org && org.pan}
                  className={`${errors.pan && "input-error"}`}
                  style={{ ...inputStyle(!!errors.pan, true), letterSpacing: "0.08em" }}
                />,
                errors.pan,
              ),
            ])}

            {/* ---------------- ADMIN ---------------- */}
            {section("admin", "Admin", "2 required", nCols, [
              fieldBox(
                "adminName",
                "Admin Name",
                true,
                <input
                  type="email"
                  id="adminName"
                  name="adminName"
                  placeholder="Full name"
                  onChange={handleChange}
                  value={org && org.adminName}
                  className={`${errors.adminName && "input-error"}`}
                  style={inputStyle(!!errors.adminName)}
                />,
                errors.adminName,
              ),
              fieldBox(
                "adminEmail",
                "Admin Email ID",
                true,
                <input
                  type="email"
                  id="adminEmail"
                  name="adminEmail"
                  placeholder="name@example.com"
                  onChange={handleChange}
                  value={org && org.adminEmail}
                  className={`${errors.adminEmail && "input-error"}`}
                  style={inputStyle(!!errors.adminEmail)}
                />,
                errors.adminEmail,
              ),
              fieldBox(
                "adminMobile",
                "Mobile Number",
                false,
                <div
                  className={`phone-container ${errors.adminMobile ? "input-error" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: 36,
                    boxSizing: "border-box",
                    background: FIELD_BG,
                    border: `1px solid ${errors.adminMobile ? ERROR : FIELD_EDGE}`,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="dc-cc"
                    style={{
                      flex: "0 0 auto",
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                      padding: "0 6px 0 10px",
                      borderRight: `1px solid ${FIELD_EDGE}`,
                      boxSizing: "border-box",
                    }}
                  >
                    <CountrySelect
                      value={country}
                      onChange={(c) => setCountry(c)}
                    />
                  </div>
                  <input
                    type="tel"
                    id="adminMobile"
                    name="adminMobile"
                    className="dc-bare"
                    placeholder="Enter mobile number"
                    value={org?.adminMobile || ""}
                    onChange={(e) =>
                      setOrg((prev) => ({ ...prev, adminMobile: e.target.value }))
                    }
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: "100%",
                      boxSizing: "border-box",
                      padding: "0 12px 0 10px",
                      margin: 0,
                      border: 0,
                      background: "transparent",
                      fontFamily: FONT,
                      fontSize: 13.5,
                      color: INK,
                      outline: "none",
                    }}
                  />
                </div>,
                errors.adminMobile,
                <div
                  style={{
                    margin: "6px 0 0",
                    fontFamily: FONT,
                    fontSize: "11.5px",
                    color: MUTED,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: "#8A97A8" }} /> This mobile
                  number will be used as admin login
                </div>,
              ),
            ])}

            {/* ---------------- SUBSCRIPTION (superadmin only) ---------------- */}
            {user &&
              section("subscription", "Subscription", "2 required", 3, [
                fieldBox(
                  "subscriptionStartDate",
                  "Subscription Start Date",
                  true,
                  <ThemedDatePicker
                    id="subscriptionStartDate"
                    name="subscriptionStartDate"
                    onChange={handleChange}
                    value={org && org.subscriptionStartDate}
                    error={!!errors.subscriptionStartDate}
                  />,
                  errors.subscriptionStartDate,
                ),
                fieldBox(
                  "subscriptionEndDate",
                  "Subscription End Date",
                  true,
                  <ThemedDatePicker
                    id="subscriptionEndDate"
                    name="subscriptionEndDate"
                    onChange={handleChange}
                    value={org && org.subscriptionEndDate}
                    error={!!errors.subscriptionEndDate}
                    min={org && org.subscriptionStartDate}
                  />,
                  errors.subscriptionEndDate,
                ),
                fieldBox(
                  "subscription",
                  "Subscription Type",
                  false,
                  <ThemedSelect
                    id="subscription"
                    name="subscription"
                    onChange={handleChange}
                    value={org && org.subscription}
                    error={!!errors.subscription}
                    placeholder="---Choose Type---"
                    options={[
                      { value: "FREE", label: "Free" },
                      { value: "BASIC", label: "Basic" },
                      { value: "PRO", label: "Pro" },
                    ]}
                  />,
                  errors.subscription,
                ),
              ])}

            {/* ---------------- PUBLIC SIGN-UP FOOTER ---------------- */}
            {!user && (
              // Plan picker + payment proof kept commented out as before.
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 2,
                    p: "12px 14px",
                    background: "#E9F7EE",
                    border: "1px solid #C4E8D2",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ width: 8, height: 8, flex: "0 0 8px", borderRadius: "50%", background: "#2FA864" }} />
                  <Typography
                    sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, lineHeight: 1.5, color: "#1B6B41", textWrap: "pretty" }}
                  >
                    Free 30 days trial - You get free access to all our premium
                    features for 30 days
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", "& > *": { flex: 1, height: "44px !important" } }}>
                  {saveButton}
                </Box>
              </>
            )}
          </form>
        </div>

        {/* ---------------- MOBILE ACTION BAR (superadmin) ---------------- */}
        {user && isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={handleBack} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </>
  );
};

export default CreateOrg;